import { useState } from 'react'
import { BellRing, LayoutGrid, Link2, List, Type } from 'lucide-react'
import Button from '../../components/Button'
import Card from '../../components/Card'
import FormRow from '../../components/FormRow'
import SegmentedControl from '../../components/SegmentedControl'
import StatusBadge from '../../components/StatusBadge'
import TextField from '../../components/TextField'
import { InfoNote } from '../../components/Form'
import { useToast } from '../../components/toastContext'
import {
  setExternalPolicyUrl,
  setPoliciesPageTitle,
  setPoliciesViewing,
} from './engagementService'
import { policiesStrings as S } from './strings'
import { serverText } from '../../lib/i18n'

/**
 * إعدادات السياسات — the legacy `SettingsTab`.
 *
 * EACH SETTING SAVES ON ITS OWN, because each is its own endpoint: there is no
 * "save the settings" action, only `UpdatePoliciesViewing`, `UpdateExternalPoliciesUrl`
 * and `UpdatePageTitle`. The viewing mode has no Save beside it — a two-way choice writes
 * when it is made; the two text settings do, because typing is not a decision until you
 * say so.
 *
 * The two notification lines are READ-ONLY. The page payload says whether a template
 * exists; editing one is the notifications screen's job, not this one's.
 */
export default function PolicySettingsPanel({ settings, onChange }) {
  const toast = useToast()
  const [externalUrl, setExternalUrl] = useState(settings.externalUrl)
  const [titleAr, setTitleAr] = useState(settings.pageTitleAr)
  const [titleEn, setTitleEn] = useState(settings.pageTitleEn)
  const [saving, setSaving] = useState('')

  const report = (outcome, fallback) => {
    if (!outcome.ok) return toast.error(outcome.message || S.actionFailed)
    toast.success(serverText(outcome.message) || fallback)
    return true
  }

  const chooseViewing = async (value) => {
    const showAsGrid = value === 'grid'
    const outcome = await setPoliciesViewing(showAsGrid)
    if (report(outcome, S.saved)) onChange({ showAsGrid })
  }

  const saveUrl = async () => {
    setSaving('url')
    const outcome = await setExternalPolicyUrl(externalUrl)
    setSaving('')
    if (report(outcome, S.saved)) onChange({ externalUrl })
  }

  const saveTitle = async () => {
    setSaving('title')
    const outcome = await setPoliciesPageTitle(titleAr, titleEn)
    setSaving('')
    if (report(outcome, S.saved)) onChange({ pageTitleAr: titleAr, pageTitleEn: titleEn })
  }

  return (
    <>
      <Card title={S.settings.viewing} subtitle={S.settings.viewingHint}>
        <SegmentedControl
          variant="underline"
          value={settings.showAsGrid ? 'grid' : 'list'}
          onChange={chooseViewing}
          items={[
            { id: 'grid', label: S.settings.viewingGrid, icon: LayoutGrid },
            { id: 'list', label: S.settings.viewingList, icon: List },
          ]}
          ariaLabel={S.settings.viewing}
        />
      </Card>

      <Card
        title={S.settings.externalUrl}
        subtitle={S.settings.externalUrlHint}
        footer={
          <Button onClick={saveUrl} disabled={saving === 'url'}>
            {S.settings.save}
          </Button>
        }
      >
        <FormRow label={S.settings.externalUrl}>
          {(id) => (
            <TextField
              id={id}
              dir="ltr"
              type="url"
              icon={Link2}
              value={externalUrl}
              onChange={setExternalUrl}
              placeholder="https://"
            />
          )}
        </FormRow>
      </Card>

      <Card
        title={S.settings.pageTitle}
        subtitle={S.settings.pageTitleHint}
        footer={
          <Button onClick={saveTitle} disabled={saving === 'title'}>
            {S.settings.save}
          </Button>
        }
      >
        <FormRow label={S.settings.pageTitleAr}>
          {(id) => <TextField id={id} icon={Type} value={titleAr} onChange={setTitleAr} />}
        </FormRow>
        <FormRow label={S.settings.pageTitleEn}>
          {(id) => (
            <TextField id={id} dir="ltr" icon={Type} value={titleEn} onChange={setTitleEn} />
          )}
        </FormRow>
      </Card>

      <Card title={S.settings.notifications}>
        <InfoNote icon={BellRing}>
          <bdi>{S.settings.newPolicy}</bdi>{' '}
          <StatusBadge tone={settings.newPolicyNotification ? 'success' : 'dark'}>
            {settings.newPolicyNotification ? S.settings.templateReady : S.settings.templateMissing}
          </StatusBadge>
        </InfoNote>
        <InfoNote icon={BellRing}>
          <bdi>{S.settings.agreement}</bdi>{' '}
          <StatusBadge tone={settings.agreementNotification ? 'success' : 'dark'}>
            {settings.agreementNotification ? S.settings.templateReady : S.settings.templateMissing}
          </StatusBadge>
        </InfoNote>
      </Card>
    </>
  )
}
